import {
  createNewProfile,
  addJobCode,
  getFeatureWiseEntitlementList,
  getProfiles,
  getRoleWithEntitlements,
  updateRoleWithEntitlements,
} from "Api/apiService";
import { ERROR_MESSAGES } from "constants/employee.constants";
import {
  normalizeFeatureEntitlements,
  normalizeRoleEntitlements,
  sleep,
} from "Utils/profileAccess.utils";

export const keyPrefix = "PROFILE_LIST_REDUCER_";
export const PROFILE_LIST_ACTIONS = {
  SET_PROFILES: keyPrefix + "SET_PROFILES",
  SET_FETCH_PROFILES_LOADER: keyPrefix + "SET_FETCH_PROFILES_LOADER",
  SET_ROLE_ENTITLEMENTS_LOADER: keyPrefix + "SET_ROLE_ENTITLEMENTS_LOADER",
  SET_SELECTED_ROLE_ENTITLEMENTS: keyPrefix + "SET_SELECTED_ROLE_ENTITLEMENTS",
  SET_FEATURE_ENTITLEMENTS: keyPrefix + "SET_FEATURE_ENTITLEMENTS",
  SET_UPDATE_ROLE_ENTITLEMENTS_LOADER:
    keyPrefix + "SET_UPDATE_ROLE_ENTITLEMENTS_LOADER",
  SHOW_TOAST: keyPrefix + "SHOW_TOAST",
  HIDE_TOAST: keyPrefix + "HIDE_TOAST",
  CLEAR_PROFILES: keyPrefix + "CLEAR_PROFILES",
  CLEAR_PROFILE_DRAWER: keyPrefix + "CLEAR_PROFILE_DRAWER",
  SET_CREATE_JOB_CODE_LOADER: keyPrefix + "SET_CREATE_JOB_CODE_LOADER",
};

export const clearProfiles = () => ({ type: PROFILE_LIST_ACTIONS.CLEAR_PROFILES });

export const clearProfileDrawer = () => ({
  type: PROFILE_LIST_ACTIONS.CLEAR_PROFILE_DRAWER,
});

export const setProfiles = (profiles) => ({
  type: PROFILE_LIST_ACTIONS.SET_PROFILES,
  payload: profiles,
});

export const setFetchProfilesLoader = (isLoading) => ({
  type: PROFILE_LIST_ACTIONS.SET_FETCH_PROFILES_LOADER,
  payload: isLoading,
});

export const setRoleEntitlementsLoader = (isLoading) => ({
  type: PROFILE_LIST_ACTIONS.SET_ROLE_ENTITLEMENTS_LOADER,
  payload: isLoading,
});

export const setUpdateRoleEntitlementsLoader = (isLoading) => ({
  type: PROFILE_LIST_ACTIONS.SET_UPDATE_ROLE_ENTITLEMENTS_LOADER,
  payload: isLoading,
});

export const setCreateJobCodeLoader = (isLoading) => ({
  type: PROFILE_LIST_ACTIONS.SET_CREATE_JOB_CODE_LOADER,
  payload: isLoading,
});

export const showToast = (message, type = "error") => ({
  type: PROFILE_LIST_ACTIONS.SHOW_TOAST,
  payload: { message, type },
});

export const hideToast = () => ({
  type: PROFILE_LIST_ACTIONS.HIDE_TOAST,
});

export const fetchAllProfiles = () => {
  return async (dispatch) => {
    try {
      dispatch(setFetchProfilesLoader(true));

      const profilesResponse = await getProfiles();

      if (!profilesResponse) {
        dispatch(setProfiles([]));
        return;
      }

      if (!Array.isArray(profilesResponse)) {
        dispatch(setProfiles([]));
        return;
      }

      if (profilesResponse && profilesResponse.length > 0) {
        dispatch(setProfiles(profilesResponse));
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || ERROR_MESSAGES.SOMETHING_WENT_WRONG;
      dispatch(showToast(errorMessage, "error"));
      dispatch(setFetchProfilesLoader(false));
    } finally {
      dispatch(setFetchProfilesLoader(false));
    }
  };
};

export const createProfile = (profileName) => {
  return async (dispatch) => {
    try {
      dispatch(setFetchProfilesLoader(true));
      const savedProfileResponse = await createNewProfile(profileName);

      if (!savedProfileResponse) {
        console.log("CreateProfile: No response return");
        return;
      } else {
        await sleep(1000);
        await dispatch(fetchAllProfiles());
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || ERROR_MESSAGES.SOMETHING_WENT_WRONG;
      dispatch(showToast(errorMessage, "error"));
      dispatch(setFetchProfilesLoader(false));
    }
  };
};

export const fetchRoleEntitlements = ({ selectedRoleId, selectedFeatureId }) => {
  return async (dispatch) => {
    try {
      dispatch(setRoleEntitlementsLoader(true));

      const getRoleWithEntitlementsResponse = await getRoleWithEntitlements({
        selectedRoleId,
        selectedFeatureId,
      });

      if (!getRoleWithEntitlementsResponse) {
        console.log("fetchRoleEntitlements: No response from API");
        return;
      }

      const normalizedRoleWithEntitlements = normalizeRoleEntitlements(
        getRoleWithEntitlementsResponse
      );

      dispatch({
        type: PROFILE_LIST_ACTIONS.SET_SELECTED_ROLE_ENTITLEMENTS,
        payload: normalizedRoleWithEntitlements,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || ERROR_MESSAGES.SOMETHING_WENT_WRONG;
      dispatch(showToast(errorMessage, "error"));
      dispatch(setRoleEntitlementsLoader(false));
    } finally {
      dispatch(setRoleEntitlementsLoader(false));
    }
  };
};

export const fetchFeatureWiseEntitlementList = ({ selectedFeatureId }) => {
  return async (dispatch) => {
    try {
      dispatch(setRoleEntitlementsLoader(true));

      const getFeatureWiseEntitlement = await getFeatureWiseEntitlementList({
        selectedFeatureId,
      });

      if (!getFeatureWiseEntitlement) {
        console.log("fetchRoleEntitlements: No response from API");
        return;
      }

      const normalizedFeatureWiseEntitlement = normalizeFeatureEntitlements(
        getFeatureWiseEntitlement
      );

      dispatch({
        type: PROFILE_LIST_ACTIONS.SET_FEATURE_ENTITLEMENTS,
        payload: normalizedFeatureWiseEntitlement,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || ERROR_MESSAGES.SOMETHING_WENT_WRONG;
      dispatch(showToast(errorMessage, "error"));
      dispatch(setRoleEntitlementsLoader(false));
    }
  };
};

export const updateRoleEntitlementsApi = ({
  selectedRoleId,
  selectedFeatureId,
  payload,
}) => {
  return async (dispatch) => {
    try {
      dispatch(setUpdateRoleEntitlementsLoader(true));

      const updateRoleEntitlementsApiResponse = await updateRoleWithEntitlements(
        {
          selectedRoleId,
          selectedFeatureId,
          payload,
        }
      );

      if (!updateRoleEntitlementsApiResponse) {
        console.log("updateRoleEntitlements: No response return");
        return;
      }

      dispatch(fetchRoleEntitlements({ selectedRoleId, selectedFeatureId }));
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || ERROR_MESSAGES.SOMETHING_WENT_WRONG;
      dispatch(showToast(errorMessage, "error"));
      dispatch(setUpdateRoleEntitlementsLoader(false));
    } finally {
      dispatch(setUpdateRoleEntitlementsLoader(false));
    }
  };
};

export const createJobCode = (jobCodePayload) => {
  return async (dispatch) => {
    try {
      dispatch(setCreateJobCodeLoader(true));

      await addJobCode(jobCodePayload);

      dispatch(showToast("Job code created successfully", "success"));
      return true;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || ERROR_MESSAGES.SOMETHING_WENT_WRONG;
      dispatch(showToast(errorMessage, "error"));
      return false;
    } finally {
      dispatch(setCreateJobCodeLoader(false));
    }
  };
};